/**
 * 교육 화면이 읽는 TBM 문서 파생 — 구(`TBMContent`)/신(COMPOSED `tbm-meeting-log`) **두 형상을
 * 한 가지 형태로 수렴**시키는 순수 함수. 판정부는 여기 하나이고 화면은 그것만 읽는다
 * (`packages/shared/src/utils/delegation-progress.ts`의 `deriveDelegationProgress`와 같은 규약).
 *
 * ── 왜 파생 하나로 모으는가
 * 읽는 자리가 화면에 흩어져 있으면 형상이 하나 늘 때마다 **놓치는 자리가 생긴다**. 실제로
 * `tbm.content?.`가 교육 화면에만 20자리에 흩어져 있었고, 그중 `hazardItems` 하나를 놓친 것이
 * 「안전조치 확인 표가 빈 채로 교육 완료 가능」이라는 규정 위반을 만들었다
 * (결정 `docs/decisions/2026-09-08-composed-education-runtime.md`).
 *
 * ── ⚠️ 읽기에는 형상 판별을 쓰지 않는다
 * 두 키 집합이 서로 **배타**다(구엔 `-src`가 없고 composed엔 `hazardItems`가 없다). 그래서 이
 * 파생은 "어느 키가 있나"로만 고른다. 정체 값으로 갈랐다가 **어느 쪽으로 오판해도 기준 목록이
 * 비어** 미선택 차단이 풀리기 때문이다 — 안전 축을 정체 값의 정확성에 걸지 않는다
 * (결정 「착수 중 확인된 것」, 2026-09-08). 쓰기 축 판별은 두지 않는다(아래 주석).
 *
 * ── 형상별 키 (dev DB + 템플릿 선언 대조, 2026-09-08)
 * `workType`·`workDescription`·`tbmLocation`은 **두 형상이 같은 키**다. COMPOSED의 `-src` 값은
 * 참조가 아니라 **해소된 행 배열이 그대로** 저장돼 있어(`prefill`/`compose` 산출이 그대로
 * `content`에 실린다) 카탈로그 해소를 흉내 낼 필요가 없다.
 *
 * | 구 TBM | COMPOSED | 비고 |
 * |---|---|---|
 * | `hazardItems[]{hazard,measure}` | `content['tbmHazards-src'][]{hazard,measure,photoUrl}` | `measure`는 arrayJoin(', ') 결과라 이미 한 줄 문자열 |
 * | `photos[]{photoId,photoUrl,findings}` | `content['tbmPhotos-src'][]{rowId,url,caption,findings}` | 자식 `findings`는 `{riskDescription,reductionMeasures}` |
 * | `criticalHazard{hazard,measure}` | `criticalHazardText` + `criticalHazardMeasures` | |
 * | `tag`·`leader` | 없음 | COMPOSED엔 그 개념이 없다 → 자연히 미표시 |
 * | `safetyChecks`/`dailyInspectionResult`/`specialNotes` | 없음 | COMPOSED는 **세션 축**(템플릿이 `tbmSessions-src`로 읽는다) |
 *
 * 구 전용 키는 **조건 없이 읽어도 안전하다** — composed content에 그 키가 아예 없어 undefined로
 * 떨어진다. 그래서 이 파생에는 "구 문서인가" 분기가 없다.
 */
import type {
  TBM,
  TBMContent,
  TBMCriticalHazard,
  TBMHazardItem,
  TBMLeader,
  TBMPhoto,
  TBMPhotoFinding,
  TBMSafetyCheck,
} from './tbm';

/** COMPOSED 잠재위험요인 표의 데이터셋 키(`tbm-meeting-log.template.ts`의 `HAZARDS_SOURCE_KEY`). */
export const COMPOSED_HAZARD_ROWS_KEY = 'tbmHazards-src';
/** COMPOSED 본문 사진 카드의 데이터셋 키(`tbm-meeting-log.template.ts`의 `PHOTOS_SOURCE_KEY`). */
export const COMPOSED_PHOTO_ROWS_KEY = 'tbmPhotos-src';

/** 교육 화면이 쓰는 문서 값 — 구/신 어느 형상에서 왔든 같은 형태. */
export interface TbmEducationDoc {
  workDescription?: string;
  workType?: string;
  tbmLocation?: string;
  /** 잠재위험요인·대책. 안전조치 확인 기준 목록(`deriveSafetyCheckBase`)의 원료이기도 하다. */
  hazardItems: TBMHazardItem[];
  photos: TBMPhoto[];
  criticalHazard?: TBMCriticalHazard;
  /** 구 문서 전용 — COMPOSED엔 태그 개념이 없다(미표시). */
  tag?: string;
  /**
   * 「TBM 담당자」 표시값 — 구 문서의 `content.leader`를 그대로 싣는다(COMPOSED엔 키가 없어
   * 자연히 undefined).
   *
   * ⚠️ **이건 빠뜨린 게 아니라 미룬 것이다.** 진행자 성명의 정본 체인은
   * `report-composer.service.ts`의 `assembleTbmEducationResolved`(2325~2345):
   * `첫 세션 educator → tbmEducatorContact → direct 토큰 → content.leader`이고,
   * 이 화면은 그 **마지막 층만** 읽는다(= 순서가 정본과 다르다). 9/8 결정이 없앤 것은
   * *서명*의 author 폴백이지 이 성명/소속/직책 링크가 아니므로 층 자체는 유효하다.
   *
   * 이번 범위에서 재배열하지 않는 이유 셋:
   *  (a) 이 배선의 성립에 불필요하다 — COMPOSED는 어차피 이 칸이 빈다.
   *  (b) 구 TBM 화면 **동작을 바꾸는 개선**이라 회귀 위험을 여기서 질 이유가 없다(구는 걷어낸다).
   *  (c) `tbmEducatorContact`가 web `TBM`에 없어 지금 옮겨도 반쪽 체인이 된다.
   * COMPOSED에서 이 칸이 비는 실해는 없다 — 헤더에 `adapter.educatorName`이 이미 있다.
   */
  leader?: TBMLeader;
  /** 구 텍스트 방식 fallback(사진·hazardItems가 둘 다 없는 옛 문서). COMPOSED는 늘 빈 배열. */
  hazards: string[];
  safetyMeasures: string[];
  /**
   * 구 문서가 **content 축**에 저장한 교육 기록. COMPOSED는 이 값들이 `EducationSession` 행에
   * 있으므로 키 자체가 없다 — 여기서 지어내면 세션 기록과 두 정본이 생긴다.
   */
  savedSafetyChecks?: TBMSafetyCheck[];
  savedDailyInspectionResult?: string;
  savedSpecialNotes?: string;
}

// ⚠️ **쓰기 축 형상 판별을 만들지 말 것.** 교육 기록은 늘 **세션 축**이고 인쇄 미리보기는 늘 문서
// 전체다. 구 형상으로 오판하면 composed content를 통째로 덮어써 되돌릴 수 없는데, 판별할 축
// 자체가 없으므로(모든 문서가 Composer 양식) 참을 돌려줄 근거도 없다.

/** 배열 안의 객체 행만. `-src` 값이 아직 없거나 형상이 어긋나면 빈 목록(지어내지 않는다). */
function asRows(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object');
}

/** 공백뿐인 값은 없는 것으로 — 빈 문자열이 폴백 체인을 조기 종료시키지 않게. */
function str(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value.trim() === '' ? undefined : value;
}

function strArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
}

/** COMPOSED 사진 행의 자식 `findings` → 구 표시 형상. severity는 COMPOSED에 없다(지어내지 않는다). */
function composedFindings(value: unknown): TBMPhotoFinding[] | undefined {
  const findings = asRows(value)
    .map((f): TBMPhotoFinding => {
      const measures = strArray(f.reductionMeasures);
      return {
        description: str(f.riskDescription) ?? '',
        category: str(f.riskCategory) ?? str(f.category),
        measures: measures.length > 0 ? measures : undefined,
      };
    })
    .filter((f) => f.description !== '' || (f.measures?.length ?? 0) > 0);
  return findings.length > 0 ? findings : undefined;
}

/** `tbmHazards-src` 행 → 표시 형상. 키가 없으면 빈 목록(구 문서라는 뜻이다). */
function composedHazardItems(content: TBMContent): TBMHazardItem[] {
  return asRows(content[COMPOSED_HAZARD_ROWS_KEY])
    .map((row): TBMHazardItem => ({
      hazard: str(row.hazard) ?? '',
      // 템플릿이 `arrayJoin: ', '`로 접어 저장하므로 이미 한 줄 문자열이다.
      measure: str(row.measure) ?? '',
      photoUrl: str(row.photoUrl),
    }))
    // 손 추가 직후의 빈 행(둘 다 빈 값)은 표시할 것이 없다.
    .filter((h) => h.hazard !== '' || h.measure !== '');
}

/** `tbmPhotos-src` 행 → 표시 형상. 키가 없으면 빈 목록. */
function composedPhotos(content: TBMContent): TBMPhoto[] {
  return asRows(content[COMPOSED_PHOTO_ROWS_KEY])
    .map((row, idx): TBMPhoto => ({
      photoId: str(row.rowId) ?? str(row.url) ?? `${COMPOSED_PHOTO_ROWS_KEY}-${idx}`,
      photoUrl: str(row.url) ?? '',
      caption: str(row.caption),
      findings: composedFindings(row.findings),
    }))
    // URL 없는 행은 깨진 이미지가 될 뿐이다.
    .filter((p) => p.photoUrl !== '');
}

/** COMPOSED 중점위험요인 — 두 단수 칸을 합친다. 둘 다 비면 undefined(구 키를 쓰라는 뜻). */
function composedCriticalHazard(content: TBMContent): TBMCriticalHazard | undefined {
  const hazard = str(content.criticalHazardText);
  const measure = str(content.criticalHazardMeasures);
  if (!hazard && !measure) return undefined;
  return { hazard: hazard ?? '', measure: measure ?? '' };
}

/**
 * 교육 화면용 문서 파생 — 구/신 두 형상에서 같은 형태를 만든다.
 *
 * 값이 없으면 **빈 문서**를 돌려준다(호출부가 옵셔널 체이닝을 다시 흩뿌리지 않게). 빈 문서는
 * "값이 없다"는 사실 그대로다.
 */
export function resolveTbmEducationDoc(tbm: TBM | undefined | null): TbmEducationDoc {
  const content = tbm?.content;
  if (!content) {
    return { hazardItems: [], photos: [], hazards: [], safetyMeasures: [] };
  }

  // 신 키가 있으면 그쪽, 없으면 구 키 — 두 집합이 배타라 순서가 결과를 바꾸지 않는다.
  // (형상을 `type`으로 고르지 않는 이유는 위 모듈 주석.)
  const composedHazards = composedHazardItems(content);
  const composedPhotoRows = composedPhotos(content);

  return {
    // 두 형상이 같은 키를 쓰는 값들 — 가를 것이 없다.
    workDescription: str(content.workDescription),
    workType: str(content.workType),
    tbmLocation: str(content.tbmLocation),

    hazardItems: composedHazards.length > 0 ? composedHazards : (content.hazardItems ?? []),
    photos: composedPhotoRows.length > 0 ? composedPhotoRows : (content.photos ?? []),
    criticalHazard: composedCriticalHazard(content) ?? content.criticalHazard,

    // 구 전용 키 — composed content엔 아예 없어 조건 없이 읽어도 undefined다.
    tag: content.tag,
    leader: content.leader,
    hazards: content.hazards ?? [],
    safetyMeasures: content.safetyMeasures ?? [],
    savedSafetyChecks: content.safetyChecks,
    savedDailyInspectionResult: content.dailyInspectionResult,
    savedSpecialNotes: content.specialNotes,
  };
}
